import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useStyles } from './SubProjectTable.styles';
import { ManageClientModal } from '@modules/EvaluationModule/pages/ClientContactPage/components/ManageClientModal/ManageClientModal';
import {
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PermContactCalendarOutlinedIcon from '@mui/icons-material/PermContactCalendarOutlined';
import { ClientProjectData } from '@modules/EvaluationModule/interfaces/ClientProjectData';
import { AllSubProjectFilter } from '../AllSubProjectFilter/AllSubProjectFilter';
import { useDispatch, useSelector } from 'react-redux';
import { clientSubProjectFilterSelector } from '../../redux/selectors';
import { setClientSubProjectFilters } from '../../redux/actions';
import { WorkstreamStatus } from '../../enums/ContactFilterOptions';
import { useClientWorkstreamsData } from '../../hooks/useClientWorkstreamsData';
import { mergeWorkstreams } from '../../helper/mergeWorkstreams';

import { SubProjectTableRow } from './SubProjectTableRow';

interface Props {
  choosenProject: ClientProjectData;
}

export const SubProjectTable: React.FC<Props> = ({ choosenProject }) => {
  const styles = useStyles();
  // DB rows (from /my-projects-list) render at once; the per-client Zoho check only adds the
  // workstreams the DB lacks and the pseudo row's contacts. A TTL-expired recheck keeps its
  // previous data (stale-while-revalidate), so added rows don't flicker out.
  const zohoWorkstreams = useClientWorkstreamsData(choosenProject.id).data;
  const workstreams = useMemo(
    () => mergeWorkstreams(choosenProject.id, choosenProject.workstreams, zohoWorkstreams),
    [choosenProject.id, choosenProject.workstreams, zohoWorkstreams]
  );
  const realWorkstreams = useMemo(() => workstreams.filter((ws) => ws.isWorkstream), [workstreams]);
  const clientSubProjectFilter = useSelector(clientSubProjectFilterSelector);
  const dispatch = useDispatch();

  const includeClosedWorkstreams = useCallback(() => {
    dispatch(setClientSubProjectFilters({ includeClosedWorkstreams: true }));
  }, []);

  const filteredWorkstreams = useMemo(() => {
    return workstreams.filter((ws) => {
      if (ws.status === WorkstreamStatus.Closed && !clientSubProjectFilter.includeClosedWorkstreams) {
        return false;
      }
      return true;
    });
  }, [workstreams, clientSubProjectFilter.includeClosedWorkstreams]);

  // Workstream titles stick right below the sticky column header, so they need its height —
  // re-measured whenever it changes (font, padding, zoom), written as a CSS variable (no re-render).
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableHeadRef = useRef<HTMLTableSectionElement>(null);
  useEffect(() => {
    const container = tableContainerRef.current;
    const head = tableHeadRef.current;
    if (!container || !head || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      container.style.setProperty('--table-head-height', `${head.offsetHeight}px`);
    });
    observer.observe(head);
    return () => observer.disconnect();
  }, []);

  const numberOfFilters = useMemo(() => {
    return Object.keys(clientSubProjectFilter).reduce((total, key) => {
      const value = clientSubProjectFilter[key as keyof typeof clientSubProjectFilter];
      if (value === true || (Array.isArray(value) && value.length > 0)) return total + 1;
      return total;
    }, 0);
  }, [clientSubProjectFilter]);

  return (
    <div className={styles.root}>
      <div className={styles.workstreamView}>
        <Alert severity="warning" icon={false} className={styles.zohoWarning}>
          <Stack flexDirection="row" columnGap="10px" alignItems="center">
            <PermContactCalendarOutlinedIcon color="disabled" />
            <Typography variant="body2">
              Recently created client contacts take 1-2 minutes to be loaded from ZohoCRM
            </Typography>
          </Stack>
        </Alert>
        <div className={styles.headerComponent}>
          <Typography variant="h1" mb={4}>
            {choosenProject.name}
            <ManageClientModal choosenProject={choosenProject} workstreams={realWorkstreams} />
          </Typography>
          <AllSubProjectFilter noOfFilters={numberOfFilters} />
        </div>
        <TableContainer className={styles.subProjectTable} ref={tableContainerRef}>
          <Table stickyHeader aria-label="table" sx={{ tableLayout: 'fixed' }}>
            <TableHead ref={tableHeadRef}>
              <TableRow className={styles.tableHeader}>
                <TableCell align="left" width="30%">
                  Point of contact
                </TableCell>
                <TableCell align="left" width="15%">
                  Title
                </TableCell>
                <TableCell align="left" width="18%">
                  Status
                </TableCell>
                <TableCell align="left" width="15%">
                  Location
                </TableCell>
                <TableCell align="left" width="12%">
                  Feedbacks
                </TableCell>
                <TableCell align="left" width="10%"></TableCell>
              </TableRow>
            </TableHead>
            {filteredWorkstreams.length > 0 &&
              filteredWorkstreams.map((workstream) => {
                return (
                  <TableBody key={workstream.zoho_crm_id}>
                    <SubProjectTableRow
                      choosenProject={choosenProject}
                      key={workstream.zoho_crm_id}
                      workstream={workstream}
                      workstreams={realWorkstreams}
                    />
                  </TableBody>
                );
              })}
            {filteredWorkstreams.length === 0 && workstreams.length > 0 && (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                      No active workstreams.{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        color="primary"
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={includeClosedWorkstreams}
                      >
                        Show closed workstreams
                      </Typography>
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};
